'use client';

import { Select } from '@base-ui/react/select';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { type CSSProperties, useCallback } from 'react';
import { MdCheck } from 'react-icons/md';
import { shallow } from 'zustand/shallow';
import { ExpandIcon } from '../ExpandIcon';
import { useRipplePress } from '../useRipplePress';
import type { SingleSelectOption, singleSelectStoreProps } from './types';

export type { SingleSelectOption, singleSelectStoreProps };

type Props<TFilterOptions extends string> = {
  label: TFilterOptions;
  options: SingleSelectOption[];
  selectedOption?: string;
  onChange: (selected: string) => void;
  isStickyBar?: boolean;
  scrollBackToMainBar?: () => void;
  useStore: singleSelectStoreProps<TFilterOptions>;
  placeholder?: string;
};

const triggerClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: '2',
  rowGap: '2',
  borderTopLeftRadius: '[64px]',
  borderTopRightRadius: '[64px]',
  borderBottomRightRadius: '[64px]',
  borderBottomLeftRadius: '[64px]',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  minHeight: '10',
  minWidth: '[200px]',
  cursor: 'pointer',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderBottomColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
  backgroundColor: 'white',
  fontFamily: '[inherit]',
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'hidden',
  _focus: {
    outlineStyle: 'none',
  },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
  _after: {
    content: '""',
    display: 'block',
    position: 'absolute',
    width: 'full',
    height: 'full',
    top: '0',
    left: '0',
    pointerEvents: 'none',
    backgroundImage: '[radial-gradient(circle,token(colors.green.main) 10%,transparent 10.01%)]',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '[50%]',
    backgroundPositionY: '[50%]',
    // No resting `transition` here: the async Panda stylesheet applies these
    // properties over the already-painted (inline critical CSS) trigger, and a
    // resting transition would animate that change into a green ripple on load.
    // The ripple is driven by the `ripple` keyframe, which useRipplePress runs
    // by toggling `data-rippling` on pointer/keyboard activation.
    transform: '[scale(0)]',
    opacity: '0',
  },
  '&[data-rippling]::after': {
    animationName: 'ripple',
    animationDuration: '[0.4s]',
    animationTimingFunction: 'ease-out',
  },
});

const triggerActiveClass = css({
  backgroundColor: 'green.light',
  borderTopWidth: 'medium',
  borderRightWidth: 'medium',
  borderBottomWidth: 'medium',
  borderLeftWidth: 'medium',
  borderTopColor: 'green.main',
  borderRightColor: 'green.main',
  borderBottomColor: 'green.main',
  borderLeftColor: 'green.main',
});

const triggerLabelClass = css({
  textStyle: 'filterItem',
  color: 'var(--label-color)',
  overflowX: 'hidden',
  overflowY: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const triggerLabelActiveClass = css({ color: 'var(--label-color)' });

const popupClass = css({
  backgroundColor: 'white',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
  borderBottomRightRadius: '3',
  borderBottomLeftRadius: '3',
  boxShadow: '[0 4px 12px rgba(0,0,0,0.15)]',
  minWidth: '[200px]',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  outlineStyle: 'none',
});

const popupHeaderClass = css({
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  textStyle: 'filterItem',
  color: 'neutral.dark',
});

const listClass = css({
  maxHeight: '[200px]',
  overflowY: 'auto',
});

const itemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  cursor: 'pointer',
  fontSize: 'sm',
  backgroundColor: 'neutral.white',
  color: 'neutral.dark',
  outlineStyle: 'none',
  height: '9',
  '&[data-highlighted]': { backgroundColor: 'neutral.lightest' },
  '&[data-selected]': { fontWeight: 'medium' },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: 'blue.light',
    outlineOffset: '[-2px]',
  },
});

const itemActiveClass = css({
  backgroundColor: 'neutral.white',
  '&[data-highlighted]': { backgroundColor: 'neutral.white' },
  '&[data-highlighted]:hover': { backgroundColor: 'neutral.lightest' },
});

const itemIndicatorWrapperClass = css({
  display: 'flex',
  width: '6',
  minWidth: '6',
  justifyContent: 'flex-end',
  alignItems: 'center',
});

const itemIndicatorClass = css({
  color: 'green.main',
});

export const SingleSelectDropdown = <TFilterOptions extends string>({
  label,
  options,
  selectedOption,
  onChange,
  isStickyBar = false,
  scrollBackToMainBar,
  useStore,
  placeholder,
}: Props<TFilterOptions>) => {
  const handleOpenKey = isStickyBar ? 'stickyBar' : 'normalBar';
  const isOpen = useStore(state => state.openedFilters[handleOpenKey][label], shallow);
  const handleOpenedFilters = useStore(state => state.handleOpenedFilters, shallow);

  const isSelected = !!selectedOption;
  const displayText = options.find(o => o.value === selectedOption)?.label ?? placeholder ?? label;
  const { ref: rippleRef } = useRipplePress();

  const handleValueChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      handleOpenedFilters(label, handleOpenKey, false);
      setTimeout(() => {
        onChange(value);
        if (scrollBackToMainBar) scrollBackToMainBar();
      }, 100);
    },
    [onChange, scrollBackToMainBar, handleOpenedFilters, label, handleOpenKey],
  );

  return (
    <Select.Root
      value={selectedOption ?? ''}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={open => handleOpenedFilters(label, handleOpenKey, open)}
      modal={false}
    >
      <Select.Trigger
        ref={rippleRef}
        aria-haspopup="listbox"
        className={cx(triggerClass, isSelected && triggerActiveClass)}
      >
        <span
          style={
            {
              '--label-color': isSelected ? '#008254' : '#64666A',
            } as CSSProperties
          }
          className={cx(triggerLabelClass, isSelected && triggerLabelActiveClass)}
        >
          {displayText}
        </span>
        <Select.Icon>
          <ExpandIcon width={15} color={isSelected ? '#008254' : '#64666A'} />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Positioner
          sideOffset={8}
          align="start"
          alignItemWithTrigger={false}
          style={{ zIndex: 200 }}
        >
          <Select.Popup className={popupClass}>
            <div className={popupHeaderClass}>{label}</div>
            <Select.List className={listClass}>
              {options.map(({ label: optLabel, value, count }) => (
                <Select.Item
                  key={value}
                  value={value}
                  className={cx(itemClass, isSelected && itemActiveClass)}
                >
                  <span>
                    <Select.ItemText className={cx(isSelected && css({ fontWeight: 'normal' }))}>
                      {optLabel}
                    </Select.ItemText>
                    {count !== undefined && (
                      <span style={{ fontSize: 12, marginLeft: 4, color: '#64666A' }}>
                        ({count})
                      </span>
                    )}
                  </span>
                  <span className={itemIndicatorWrapperClass}>
                    <Select.ItemIndicator className={itemIndicatorClass}>
                      <MdCheck size={16} />
                    </Select.ItemIndicator>
                  </span>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
};
